import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { canWith } from '../utils/rbacPermissions';
import {
  createConversation,
  listConversations,
  listMessages,
  markRead,
  searchUsers,
  sendMessage,
} from '../services/chatService';
import { postFlowIaChat } from '../services/flowIaService';

export function Chat() {
  const { t, locale, permSummary, isProductionUi } = useApp();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userHits, setUserHits] = useState([]);
  const [mobileThread, setMobileThread] = useState(false);
  const pollRef = useRef(null);

  const canView = canWith(permSummary, 'chat', 'view', isProductionUi);
  const canMessage = canWith(permSummary, 'chat', 'message', isProductionUi);

  const loadConversations = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(false);
    try {
      const r = await listConversations();
      setConversations(r.data || []);
    } catch {
      setError(true);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  const loadMessages = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await listMessages(id, { limit: 50 });
      setMessages(r.data.items || []);
      await markRead(id);
    } catch {
      setMessages([]);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);
    pollRef.current = window.setInterval(() => loadMessages(activeId), 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeId, loadMessages]);

  useEffect(() => {
    if (!userQuery.trim()) {
      setUserHits([]);
      return;
    }
    const tmo = setTimeout(() => {
      searchUsers(userQuery).then((r) => setUserHits(r.data || [])).catch(() => setUserHits([]));
    }, 300);
    return () => clearTimeout(tmo);
  }, [userQuery]);

  const startDirect = async (peerUserId) => {
    const r = await createConversation({ type: 'direct', peer_user_id: peerUserId });
    const id = r.data.conversation.id;
    await loadConversations();
    setActiveId(id);
    setMobileThread(true);
    setUserQuery('');
    setUserHits([]);
  };

  const onSend = async () => {
    const text = draft.trim();
    if (!text || !activeId || !canMessage) return;
    await sendMessage(activeId, { body: text });
    setDraft('');
    await loadMessages(activeId);
    await loadConversations();
  };

  const improveDraftWithFlowIa = async () => {
    const text = draft.trim();
    if (!text) return;
    try {
      const res = await postFlowIaChat({
        screen: 'chat',
        question: t('chatPage.flowIaImprovePrompt'),
        locale,
        context: { draft: text.slice(0, 2000) },
        history: [],
      });
      const improved = (res?.answer || '').trim();
      if (improved) setDraft(improved);
    } catch {
      /* ignore */
    }
  };

  const summarizeWithFlowIa = async () => {
    if (!activeId || messages.length === 0) return;
    const blob = messages.map((m) => m.body).join('\n').slice(0, 3000);
    try {
      const res = await postFlowIaChat({
        screen: 'chat',
        question: t('chatPage.flowIaSummarizePrompt'),
        locale,
        context: { transcript: blob },
        history: [],
      });
      const summary = (res?.answer || '').trim();
      if (summary) setDraft(summary);
    } catch {
      /* ignore */
    }
  };

  if (!canView) {
    return <div className="empty-state empty-state--premium">{t('chatPage.forbidden')}</div>;
  }

  const activeConv = conversations.find((c) => c.conversation.id === activeId);

  return (
    <div className={`chat-page${mobileThread ? ' chat-page--thread-open' : ''}`}>
      <aside className="chat-page__list panel panel--elevated">
        <h1 className="chat-page__title">{t('chatPage.title')}</h1>
        <input
          className="input chat-page__search"
          placeholder={t('chatPage.searchPeople')}
          value={userQuery}
          onChange={(e) => setUserQuery(e.target.value)}
        />
        {userHits.length > 0 ? (
          <ul className="chat-user-hits">
            {userHits.map((u) => (
              <li key={u.user_id}>
                <button type="button" className="chat-user-hits__btn" onClick={() => startDirect(u.user_id)}>
                  {u.name}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {loading ? (
          <p className="panel__muted">{t('common.loading')}</p>
        ) : error ? (
          <div className="empty-state empty-state--boxed">
            <p>{t('chatPage.errorLoad')}</p>
            <button type="button" className="btn btn--sm btn--secondary" onClick={loadConversations}>
              {t('common.retry')}
            </button>
          </div>
        ) : conversations.length === 0 ? (
          <p className="panel__muted">{t('chatPage.empty')}</p>
        ) : (
          <ul className="chat-conv-list">
            {conversations.map((row) => {
              const c = row.conversation;
              const label =
                c.type === 'direct'
                  ? t('chatPage.directLabel')
                  : c.name || t('chatPage.unnamed');
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`chat-conv-list__item${activeId === c.id ? ' is-active' : ''}`}
                    onClick={() => {
                      setActiveId(c.id);
                      setMobileThread(true);
                    }}
                  >
                    <span className="chat-conv-list__name">{label}</span>
                    {row.unread_count > 0 ? <span className="chat-conv-list__badge">{row.unread_count}</span> : null}
                    {row.last_message_preview ? (
                      <span className="chat-conv-list__preview text-muted text-sm">{row.last_message_preview}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      <section className="chat-page__thread panel panel--elevated">
        {!activeId ? (
          <p className="panel__muted chat-page__pick">{t('chatPage.pickConversation')}</p>
        ) : (
          <>
            <header className="chat-thread__head">
              <button type="button" className="chat-thread__back btn btn--ghost btn--sm" onClick={() => setMobileThread(false)}>
                {t('chatPage.back')}
              </button>
              <strong>{activeConv?.conversation?.name || t('chatPage.directLabel')}</strong>
              <button type="button" className="btn btn--ghost btn--sm" onClick={summarizeWithFlowIa}>
                {t('chatPage.flowIaSummarize')}
              </button>
            </header>
            <div className="chat-thread__messages">
              {messages.map((m) => (
                <div key={m.id} className="chat-bubble">
                  <p className="chat-bubble__body">{m.body}</p>
                  {m.edited_at ? <span className="chat-bubble__edited text-muted text-sm">{t('chatPage.edited')}</span> : null}
                </div>
              ))}
            </div>
            {canMessage ? (
              <div className="chat-composer-wrap">
                <div className="chat-composer">
                  <input
                    className="input chat-composer__input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={t('chatPage.composerPlaceholder')}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), onSend())}
                  />
                  <button type="button" className="btn btn--primary btn--sm" onClick={onSend}>
                    {t('chatPage.send')}
                  </button>
                </div>
                <button type="button" className="btn btn--ghost btn--sm chat-composer__ia" onClick={improveDraftWithFlowIa}>
                  {t('chatPage.flowIaImprove')}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
